import { useState, useEffect, useCallback } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ptBR } from "date-fns/locale";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { AlertOctagon, SearchCheck, CheckCircle2, XCircle, CalendarDays } from "lucide-react";

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const PrazoPicker = ({ value, onChange, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm" className="w-40 justify-start font-normal text-slate-600" data-testid={testid}>
        <CalendarDays className="h-4 w-4 mr-2 text-slate-400" />
        {value ? new Date(value + "T00:00:00").toLocaleDateString("pt-BR") : "Prazo"}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar mode="single" locale={ptBR}
        selected={value ? new Date(value + "T00:00:00") : undefined}
        onSelect={(d) => d && onChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`)} />
    </PopoverContent>
  </Popover>
);

function ApuracoesTab() {
  const [apuracoes, setApuracoes] = useState([]);
  const carregar = useCallback(() => axios.get(`${API}/logistica/apuracoes`).then(r => setApuracoes(r.data)).catch(() => {}), []);
  useEffect(() => { carregar(); }, [carregar]);

  const resolver = async (ap, resolucao) => {
    const obs = resolucao === "perda" ? window.prompt("Observação da perda (opcional):") : null;
    try {
      const r = await axios.post(`${API}/logistica/apuracoes/${ap.id}/resolver`, { resolucao, observacao: obs });
      toast.success(r.data.message);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao resolver")); }
  };

  const abertas = apuracoes.filter(a => a.status === "em_apuracao");
  const resolvidas = apuracoes.filter(a => a.status !== "em_apuracao");
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Itens <b>OF (faltantes)</b> ficam "Em apuração" até a Logística resolver: <b>Encontrado</b> (volta ao estoque/depósito) ou <b>Perda</b> (baixa com ocorrência e prejuízo).</p>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {abertas.length === 0 ? <p className="px-4 py-8 text-center text-slate-400 text-sm">Nenhuma apuração pendente.</p> :
            abertas.map(ap => (
              <div key={ap.id} className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 flex-wrap" data-testid={`apuracao-${ap.id}`}>
                <div>
                  <p className="font-semibold text-luvcorp-navy text-sm">{ap.nome} <span className="text-xs text-slate-400">{ap.codigo}</span>
                    <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-300">Em apuração</Badge></p>
                  <p className="text-xs text-slate-500">Romaneio {ap.romaneio_numero} · {ap.obra_titulo} · qtd {ap.quantidade} · {fmtBRL((ap.valor_unitario || 0) * ap.quantidade)}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" onClick={() => resolver(ap, "encontrado")} data-testid={`btn-encontrado-${ap.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Encontrado
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => resolver(ap, "perda")} data-testid={`btn-perda-${ap.id}`}>
                    <XCircle className="h-4 w-4 mr-1" /> Perda
                  </Button>
                </div>
              </div>
            ))}
        </CardContent>
      </Card>
      {resolvidas.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Resolvidas</p>
            {resolvidas.map(ap => (
              <p key={ap.id} className="text-xs text-slate-500 border-b border-slate-50 py-1">
                {ap.nome} — Romaneio {ap.romaneio_numero} · <b className={ap.resolucao === "perda" ? "text-red-600" : "text-emerald-700"}>{ap.resolucao}</b> por {ap.resolvida_por}
              </p>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function QuadroOcorrenciasTab() {
  const [dados, setDados] = useState({ pendentes: [], registradas: [] });
  const [form, setForm] = useState({});
  const carregar = useCallback(() => axios.get(`${API}/logistica/ocorrencias-pendentes`).then(r => setDados(r.data)).catch(() => {}), []);
  useEffect(() => { carregar(); }, [carregar]);

  const setF = (id, k, v) => setForm(f => ({ ...f, [id]: { ...(f[id] || {}), [k]: v } }));
  const salvar = async (linha) => {
    const f = form[linha.id] || {};
    try {
      await axios.put(`${API}/logistica/caderno-linha/${linha.id}/ocorrencia`, {
        o_que_aconteceu: f.o_que_aconteceu || "", parou_equipe: !!f.parou_equipe,
        providencia: f.providencia || null, prazo: f.prazo || null,
      });
      toast.success(`Ocorrência registrada — FL-01 folha ${linha.folha} linha ${linha.linha_folha}`);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao registrar")); }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Quadro de ocorrências da folha FL-01 — preenchido pelo Líder/Logística para cada linha <b>DIV</b>: o que aconteceu e causa · parou equipe? · providência e prazo.</p>
      {dados.pendentes.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-slate-400 text-sm">Nenhuma linha DIV aguardando ocorrência.</CardContent></Card>
      ) : dados.pendentes.map(l => (
        <Card key={l.id} className="border-0 shadow-sm" data-testid={`ocorrencia-pendente-${l.id}`}>
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-semibold text-luvcorp-navy">
              <Badge className="bg-red-600 text-white mr-2">{l.tipo}</Badge>
              {l.item} — {l.obra_titulo} · Romaneio {l.num_ref} · <span className="text-slate-400">FL-01 folha {l.folha}, linha {l.linha_folha}</span>
            </p>
            {l.descricao_div && <p className="text-xs text-slate-500">Relato da obra: {l.descricao_div}</p>}
            <Textarea rows={2} placeholder="O que aconteceu e causa *" value={(form[l.id] || {}).o_que_aconteceu || ""}
              onChange={(e) => setF(l.id, "o_que_aconteceu", e.target.value)} data-testid={`oc-aconteceu-${l.id}`} />
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={!!(form[l.id] || {}).parou_equipe} onCheckedChange={(v) => setF(l.id, "parou_equipe", !!v)} data-testid={`oc-parou-${l.id}`} />
                Parou equipe?
              </label>
              <Input className="flex-1 min-w-40" placeholder="Providência" value={(form[l.id] || {}).providencia || ""}
                onChange={(e) => setF(l.id, "providencia", e.target.value)} data-testid={`oc-providencia-${l.id}`} />
              <PrazoPicker value={(form[l.id] || {}).prazo || ""} onChange={(v) => setF(l.id, "prazo", v)} testid={`oc-prazo-${l.id}`} />
              <Button size="sm" className="bg-luvcorp-navy text-white" onClick={() => salvar(l)} data-testid={`oc-salvar-${l.id}`}>Registrar</Button>
            </div>
          </CardContent>
        </Card>
      ))}
      {dados.registradas.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Registradas</p>
            {dados.registradas.map(l => (
              <p key={l.id} className="text-xs text-slate-500 border-b border-slate-50 py-1">
                <b>{l.tipo}</b> {l.item} (f.{l.folha} l.{l.linha_folha}) — {l.ocorrencia?.o_que_aconteceu} · {l.ocorrencia?.parou_equipe ? "PAROU equipe" : "não parou equipe"} {l.ocorrencia?.providencia ? `· Providência: ${l.ocorrencia.providencia}` : ""}
              </p>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function OcorrenciasPage() {
  return (
    <MainLayout>
      <div className="space-y-4" data-testid="ocorrencias-page">
        <h1 className="text-2xl font-bold text-luvcorp-navy flex items-center gap-2">
          <AlertOctagon className="h-6 w-6 text-luvcorp-gold" /> Ocorrências e Apurações
        </h1>
        <Tabs defaultValue="apuracoes">
          <TabsList>
            <TabsTrigger value="apuracoes" data-testid="tab-apuracoes"><SearchCheck className="h-4 w-4 mr-1" /> Apurações (OF)</TabsTrigger>
            <TabsTrigger value="quadro" data-testid="tab-quadro">Quadro de ocorrências (FL-01)</TabsTrigger>
          </TabsList>
          <TabsContent value="apuracoes"><ApuracoesTab /></TabsContent>
          <TabsContent value="quadro"><QuadroOcorrenciasTab /></TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
